import { ClientConfig, StageCondition } from '../types';

export interface DefinitionReference {
  where: 'stage' | 'healthScore' | 'entityFeatureMapping';
  label: string;
}

function walkConditions(condition: StageCondition | null | undefined, visit: (c: StageCondition) => void): void {
  if (!condition || typeof condition !== 'object') return;
  visit(condition);
  (condition.all || []).forEach((c) => walkConditions(c, visit));
  (condition.any || []).forEach((c) => walkConditions(c, visit));
  if (condition.not) walkConditions(condition.not, visit);
}

/**
 * Everything in the org config that still points at an entity or feature
 * definition. Used to warn before a definition is deleted, because a
 * dangling reference silently makes a stage unreachable or a health-score
 * component score zero.
 */
export function findDefinitionReferences(
  config: Pick<ClientConfig, 'stageConfig' | 'healthScoreConfig' | 'entityFeatureMapping'> | null | undefined,
  kind: 'entity' | 'feature',
  key: string,
): DefinitionReference[] {
  if (!config || !key) return [];
  const refs: DefinitionReference[] = [];
  const same = (a: string | undefined) => !!a && a.toLowerCase() === key.toLowerCase();

  for (const stage of config.stageConfig?.stages || []) {
    let hit = false;
    walkConditions(stage.conditions, (c) => {
      if (kind === 'entity' && same(c.entity)) hit = true;
      if (kind === 'feature' && same(c.feature)) hit = true;
    });
    if (hit) refs.push({ where: 'stage', label: `stage "${stage.displayName || stage.name}"` });
  }

  for (const comp of config.healthScoreConfig?.components || []) {
    if ((kind === 'entity' && same(comp.entity)) || (kind === 'feature' && same(comp.feature))) {
      refs.push({ where: 'healthScore', label: `health-score component "${comp.name}"` });
    }
  }

  for (const [entity, feature] of Object.entries(config.entityFeatureMapping || {})) {
    if ((kind === 'entity' && same(entity)) || (kind === 'feature' && same(feature))) {
      refs.push({ where: 'entityFeatureMapping', label: `mapping ${entity} → ${feature}` });
    }
  }

  return refs;
}

/** Remove every reference to a deleted definition so the config stays consistent. */
export function stripDefinitionReferences(
  config: Pick<ClientConfig, 'stageConfig' | 'healthScoreConfig' | 'entityFeatureMapping'>,
  kind: 'entity' | 'feature',
  key: string,
): Pick<ClientConfig, 'stageConfig' | 'healthScoreConfig' | 'entityFeatureMapping'> {
  const same = (a: string | undefined) => !!a && a.toLowerCase() === key.toLowerCase();
  const matches = (c: StageCondition) => (kind === 'entity' ? same(c.entity) : same(c.feature));

  const prune = (condition: StageCondition | null | undefined): StageCondition | null => {
    if (!condition || typeof condition !== 'object') return condition ?? null;
    if (condition.all) {
      const all = condition.all.map(prune).filter((c): c is StageCondition => !!c);
      return { ...condition, all };
    }
    if (condition.any) {
      const any = condition.any.map(prune).filter((c): c is StageCondition => !!c);
      return { ...condition, any };
    }
    if (condition.not) {
      const inner = prune(condition.not);
      return inner ? { ...condition, not: inner } : null;
    }
    return matches(condition) ? null : condition;
  };

  const stageConfig = config.stageConfig
    ? { ...config.stageConfig, stages: config.stageConfig.stages.map((s) => ({ ...s, conditions: s.conditions === null || s.conditions === undefined ? s.conditions : prune(s.conditions) })) }
    : config.stageConfig;

  const healthScoreConfig = config.healthScoreConfig
    ? { ...config.healthScoreConfig, components: config.healthScoreConfig.components.filter((c) => !((kind === 'entity' && same(c.entity)) || (kind === 'feature' && same(c.feature)))) }
    : config.healthScoreConfig;

  const entityFeatureMapping = Object.fromEntries(
    Object.entries(config.entityFeatureMapping || {}).filter(([e, f]) => !((kind === 'entity' && same(e)) || (kind === 'feature' && same(f)))),
  );

  return { stageConfig, healthScoreConfig, entityFeatureMapping };
}
